import styled from '@emotion/styled'
import { semanticVersionValidation } from '@pubstack/common/src/input'
import { PbjsDesiredVersion } from '@pubstack/common/src/kleanadsScopeConfig'
import { useAtomValue } from 'jotai'
import { FunctionComponent } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useCreateKleanadsScopeConfig, useKleanadsScopeConfigFind, useUpdateKleanadsPbjsDesired } from '~/api/admin/kleanads-config.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { BorderRadius } from '~/assets/style/tokens'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { Tooltip } from '~/components/Tooltip'
import { configurationState } from '~/state'
import { getDefaultKleanadsScopeConfig } from '~/utils/defaultAdmValues'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  padding-bottom: 20px;
`

const JsonTextArea = styled.textarea`
  ${BorderRadius.style};
  outline: none;
  resize: none;
  border: 1px solid ${Colors.Platinum};
  white-space: pre;
  min-height: 200px;
  width: 100%;
  background-color: ${Colors.Ghost};
  ${Fonts.MonoSmall};
  ${Fonts.colors.Jet};

  padding: 8px;

  &:active,
  &:focus {
    border-color: ${Colors.Silver};
  }
`

const PrebidTitle = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`

const PrebidCurrent = styled.div`
  margin: 10px;
`

const PrebidData = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  & input:not([type='checkbox'], [type='radio']) {
    width: 300px;
  }
`

const ErrorMessage = styled.div`
  ${Fonts.P2}
  ${Fonts.colors.Alert}
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const JSON_SPACE_COUNT = 2
const EMPTY_PBJS_DESIRED_VERSION: PbjsDesiredVersion = {
  desiredVersion: '',
  desiredRatio: 100,
}

export const AdminScopeAdmConfig: FunctionComponent = () => {
  const { scopeId } = useParams()

  const { KLEANADS_CDN_BUCKET } = useAtomValue(configurationState)
  const defaultKleanadsScopeConfig = getDefaultKleanadsScopeConfig(KLEANADS_CDN_BUCKET)

  const { data: scopeConfig = defaultKleanadsScopeConfig, isLoading, isFetching } = useKleanadsScopeConfigFind(scopeId ?? '')
  const { mutateAsync: createScopeConfig } = useCreateKleanadsScopeConfig(scopeId ?? '', {
    successToast: 'The scope config has been created. Please wait for 5 min before using it.',
    errorToast: 'An error has occurred, please retry later.',
  })
  const { mutateAsync: updatePbjsDesired } = useUpdateKleanadsPbjsDesired(scopeId ?? '', {
    successToast: 'The desired pbjs version will be build soon and served according to the desired ratio.',
    errorToast: 'An error has occurred, please retry later.',
  })

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: EMPTY_PBJS_DESIRED_VERSION })
  const desiredVersion = useWatch({ control, name: 'desiredVersion' })
  const desiredRatio = useWatch({ control, name: 'desiredRatio' })

  const createScopeConfigData = async () => {
    await createScopeConfig(defaultKleanadsScopeConfig)
  }

  const onSubmit: SubmitHandler<PbjsDesiredVersion> = async ({ desiredVersion, desiredRatio }) => {
    await updatePbjsDesired({ desiredVersion, desiredRatio: Number(desiredRatio) })
  }

  if (isLoading || isFetching) {
    return null
  }

  const paramsJsonValue = JSON.stringify(scopeConfig, undefined, JSON_SPACE_COUNT)
  const actualPbjsVersion = scopeConfig?.pbjs?.mainVersion
  const actualPbjsRatio = scopeConfig?.pbjs?.newRatio ? 100 - scopeConfig?.pbjs?.newRatio : 100

  const newScopeConfigButton = (
    <Button variant={'primary'} onClick={createScopeConfigData} trackingLabel={'adm-admin-scopes-detail-settings-new'}>
      New scope config
    </Button>
  )

  return scopeConfig && scopeConfig.id ? (
    <Wrapper>
      <form id={'scopeConfigForm'} onSubmit={handleSubmit(onSubmit)}>
        <PrebidTitle>
          <h2>Prebid A/B Test</h2>
          <Tooltip
            title={
              <div>
                <div>
                  <strong>How does it work ?</strong>
                </div>
                Here you can set a new prebid version with a dedicated ratio. The app will just update the <em>pbjsDesired</em> field in dynamo. Then all the rest is handled by the kleanads-prebid
                lambda/ECS and the kleanads-server.
              </div>
            }
          >
            <Icon width={'16px'} name={'info'} />
          </Tooltip>
        </PrebidTitle>
        <PrebidCurrent>
          <div>
            Current prebid version is <strong>{actualPbjsVersion}</strong> deployed to <strong>{actualPbjsRatio}</strong>%
          </div>
          {scopeConfig.pbjs && scopeConfig.pbjs.newVersion && (
            <div>
              New prebid version is <strong>{scopeConfig.pbjs.newVersion}</strong> deployed to <strong>{scopeConfig.pbjs.newRatio}</strong>%
            </div>
          )}
        </PrebidCurrent>
        <PrebidData>
          <Input
            control={control}
            name={'desiredVersion'}
            label={'New Prebid desired version'}
            required={true}
            rules={{
              pattern: { value: semanticVersionValidation, message: 'Set a valid version' },
            }}
            error={errors.desiredVersion && errors.desiredVersion.type === 'pattern' ? 'Set a valid Prebid version' : undefined}
          />
          <Input
            control={control}
            type={'number'}
            name={'desiredRatio'}
            label={'New Prebid desired ratio'}
            onKeyDown={(e) => {
              if (['e', 'E', '-', '+', ',', '.'].includes(e.key)) {
                e.preventDefault()
              }
            }}
            rules={{
              required: true,
              validate: {
                max: (value: number | string) => (Number(value) <= 100 ? true : 'Should be lower or equal 100'),
                min: (value: number | string) => (Number(value) > 0 ? true : 'Should be greater than 0'),
              },
            }}
            required={true}
            min={1}
            max={100}
            iconRight={'percent'}
          />
          <Button disabled={!desiredVersion || !desiredRatio} type={'submit'} trackingLabel={'adm-admin-scopes-detail-settings-prebid-ab-test-save'}>
            Save
          </Button>
          <div>
            {errors.desiredRatio && (
              <ErrorMessage>
                <Icon name={'alert'} width={'0.875rem'} />
                {errors.desiredRatio?.message}
              </ErrorMessage>
            )}
          </div>
        </PrebidData>
      </form>
      <h2>Scope config</h2>
      <JsonTextArea spellCheck={false} value={paramsJsonValue} rows={25} disabled={true} />
    </Wrapper>
  ) : (
    <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={'There is no scope configuration.'}>
      {newScopeConfigButton}
    </IllustrationMessage>
  )
}
